import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import type { CommonFinanceInfoType } from "../../../../../common-deprecated/types/CommonTypes";
import type {
    ParsedMonthlyRateType,
    QuoteInfoType,
} from "../../../../../common-deprecated/types/FinanceInsuranceRateTypes";
import type { FinanceUpdateTrigger } from "../../../../../common-deprecated/features/tradein/utils/tradeInAemUtilTypes";

export type FinanceRateStateType = {
    fetching: boolean;
    error: boolean;
    total: string;
    totalWithoutPromotions: string;
    info: CommonFinanceInfoType;
    quotes: {
        finance: string | null;
        insurance: string | null;
        enabled: boolean;
    };
    quoteInfo: QuoteInfoType;
    financeProductType: string;
};

export const initialState: FinanceRateStateType = {
    fetching: false,
    error: false,
    total: "0",
    totalWithoutPromotions: "0",
    info: {
        effectiveInterestRate: "",
        annualInterestRate: "",
        productName: "",
        term: "",
        disclaimer: {
            value: "",
        },
    },
    quotes: {
        enabled: false,
        finance: null,
        insurance: null,
    },
    quoteInfo: {},
    financeProductType: "",
};

export type CarConfigFinanceActionsType = ReturnType<
    | typeof initQuotes
    | typeof setFinanceRateFetchLoading
    | typeof resetQuotes
    | typeof startFinanceRateFetch
    | typeof finishFinanceRateFetch
    | typeof updateFinanceQuote
    | typeof updateInsuranceQuote
    | typeof setFinanceProductType
    | typeof resetFinanceRates
>;
export const CarConfigFinanceSlice = createSlice({
    name: "finance",
    initialState,
    reducers: {
        initQuotes(state, action: PayloadAction<{ financeQuote: string; insuranceQuote: string }>) {
            return {
                ...state,
                quotes: {
                    enabled: true,
                    finance: action.payload.financeQuote,
                    insurance: action.payload.insuranceQuote,
                },
            };
        },

        resetQuotes(state) {
            return {
                ...state,
                quotes: {
                    ...state.quotes,
                    finance: null,
                    insurance: null,
                },
            };
        },

        setFinanceRateFetchLoading(state, action: PayloadAction<boolean>) {
            return {
                ...state,
                fetching: action.payload,
            };
        },
        startFinanceRateFetch(state, _: PayloadAction<{ trigger: FinanceUpdateTrigger }>) {
            return { ...state, fetching: true, error: false };
        },
        finishFinanceRateFetch(
            state,
            action: PayloadAction<{
                result: Omit<ParsedMonthlyRateType, "unselectedItemPrices" | "selectedItemPrices">;
                trigger: FinanceUpdateTrigger;
                previousDownPayment?: number;
            }>,
        ) {
            const { result } = action.payload;
            const { total = "", totalWithoutPromotions = "", quoteInfo = {}, info } = result;

            return {
                ...state,
                fetching: false,
                info,
                total,
                totalWithoutPromotions,
                quoteInfo,
            };
        },

        setFinanceProductType(state, action: PayloadAction<{ financeProduct: string }>) {
            const { financeProduct } = action.payload;

            return {
                ...state,
                financeProductType: financeProduct ?? "",
            };
        },

        updateInsuranceQuote(state, action: PayloadAction<{ insurance: string | null; hideNotification?: boolean }>) {
            const { insurance } = action.payload;

            return {
                ...state,
                quotes: { ...state.quotes, enabled: true, insurance },
            };
        },

        updateFinanceQuote(
            state,
            action: PayloadAction<{ finance: string | null; productName?: string | null; hideNotification?: boolean }>,
        ) {
            const { finance, productName } = action.payload;

            return {
                ...state,
                quotes: { ...state.quotes, enabled: true, finance },
                info: { ...state.info, productName: productName ?? "" },
            };
        },

        resetFinanceRates(state) {
            return {
                ...state,
                fetching: false,
                error: true,
                quotes: initialState.quotes,
                quoteInfo: {},
            };
        },
    },
});

export const {
    setFinanceProductType,
    initQuotes,
    resetQuotes,
    setFinanceRateFetchLoading,
    finishFinanceRateFetch,
    startFinanceRateFetch,
    updateInsuranceQuote,
    updateFinanceQuote,
    resetFinanceRates,
} = CarConfigFinanceSlice.actions;
